import { useEffect, useId, useRef, useState, type MouseEvent } from 'react';

import { axis, timeLabel } from '../lib/stats.ts';

export type Tone = 'pos' | 'neg' | 'accent' | 'muted' | 'warn' | 'ink';

/** One series of a TimeChart, one value per time (undefined: no mark there) */
export type ChartSeries = {
  id: string;
  label: string;
  /** Bars stack by sign; `pnl` is a line green above zero and red below, shaded down to zero */
  kind: 'bar' | 'line' | 'area' | 'pnl';
  values: readonly (number | undefined)[];
  tone?: Tone;
  /** Drawn below zero (withdrawals, burns) while the tooltip shows the value as it is */
  negate?: boolean;
  /** In the tooltip only, no mark */
  hidden?: boolean;
  /** Overrides the chart's format in the tooltip (a count next to dollars) */
  format?: (v: number) => string;
};

const AXIS_W = 56;
const AXIS_H = 20;
const PAD_TOP = 8;
const MAX_BAR_HALF = 14;

/**
 * Bars, lines and areas over one time axis, hover for the values. Sized to its container, drawn with the
 * theme's colors through CSS, so the dozen charts of the Stats page read as one.
 */
export function TimeChart({
  times,
  bucketMs,
  series,
  format,
  height = 180,
  empty = 'Nothing yet',
}: {
  times: readonly number[];
  bucketMs: number;
  series: ChartSeries[];
  format: (v: number) => string;
  height?: number;
  empty?: string;
}) {
  const [hover, setHover] = useState<number>();
  const [w, setW] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId().replace(/:/g, '');

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = times.length;
  const shown = series.filter((s) => !s.hidden);
  const bars = shown.filter((s) => s.kind === 'bar');
  const drawn = (s: ChartSeries, i: number): number | undefined => {
    const v = s.values[i];
    return v === undefined || !Number.isFinite(v) ? undefined : s.negate ? -v : v;
  };
  // bars stack by sign; the axis spans the stacks and every line
  const up = new Array<number>(n).fill(0);
  const down = new Array<number>(n).fill(0);
  let lo = 0;
  let hi = 0;
  for (let i = 0; i < n; i++) {
    for (const s of bars) {
      const v = drawn(s, i) ?? 0;
      if (v >= 0) up[i]! += v;
      else down[i]! += v;
    }
    hi = Math.max(hi, up[i]!);
    lo = Math.min(lo, down[i]!);
    for (const s of shown) {
      if (s.kind === 'bar') continue;
      const v = drawn(s, i);
      if (v === undefined) continue;
      hi = Math.max(hi, v);
      lo = Math.min(lo, v);
    }
  }
  const ax = axis(lo, hi, 4);
  const pw = Math.max(0, w - AXIS_W);
  const ph = height - AXIS_H - PAD_TOP;
  const sw = n > 0 ? pw / n : pw;
  const x = (i: number) => (i + 0.5) * sw;
  const y = (v: number) => PAD_TOP + ((ax.hi - v) / (ax.hi - ax.lo)) * ph;
  const y0 = y(0);
  const half = Math.max(0.5, Math.min(sw * 0.36, MAX_BAR_HALF));
  const hasData = series.some((s) => s.values.some((v) => v !== undefined && v !== 0));

  const path = (s: ChartSeries): string => {
    let d = '';
    let pen = false;
    for (let i = 0; i < n; i++) {
      const v = drawn(s, i);
      if (v === undefined) {
        pen = false;
        continue;
      }
      d += `${pen ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      pen = true;
    }
    return d;
  };
  /** The line closed down to zero over its first and last marks */
  const area = (s: ChartSeries): string => {
    const idx: number[] = [];
    for (let i = 0; i < n; i++) if (drawn(s, i) !== undefined) idx.push(i);
    if (idx.length === 0) return '';
    const first = idx[0]!;
    const lastI = idx[idx.length - 1]!;
    return `${path(s)}L${x(lastI).toFixed(1)},${y0.toFixed(1)}L${x(first).toFixed(1)},${y0.toFixed(1)}Z`;
  };

  function onMove(e: MouseEvent<SVGSVGElement>) {
    if (n === 0 || sw === 0) return;
    const bx = e.clientX - e.currentTarget.getBoundingClientRect().left;
    setHover(Math.min(n - 1, Math.max(0, Math.floor(bx / sw))));
  }

  const tipLeft = hover !== undefined && x(hover) <= pw / 2;
  const span = n > 1 ? times[n - 1]! - times[0]! : 0;
  // hours alone would not tell the days apart on an hourly axis spanning several
  const longLabels = bucketMs < 86_400_000 && span >= 86_400_000;
  const labels: { i: number; anchor: 'start' | 'middle' | 'end' }[] =
    n === 0 ? [] : n === 1 ? [{ i: 0, anchor: 'middle' }] : [{ i: 0, anchor: 'start' }, ...(n > 4 ? [{ i: Math.floor(n / 2), anchor: 'middle' as const }] : []), { i: n - 1, anchor: 'end' }];

  return (
    <div className="tchart">
      {shown.length > 1 && (
        <div className="tchart-legend">
          {shown.map((s) => (
            <span key={s.id} className={`tone-${s.tone ?? 'accent'}`}>
              <i />
              {s.label}
            </span>
          ))}
        </div>
      )}
      <div className="tchart-plot" ref={wrap} style={{ height }}>
        {w > 0 && (
          <svg width={w} height={height} onMouseMove={onMove} onMouseLeave={() => setHover(undefined)} role="img" aria-label={shown.map((s) => s.label).join(', ')}>
            <defs>
              <clipPath id={`above${id}`}>
                <rect x="0" y="0" width={pw} height={Math.max(0, y0)} />
              </clipPath>
              <clipPath id={`below${id}`}>
                <rect x="0" y={y0} width={pw} height={Math.max(0, height - y0)} />
              </clipPath>
            </defs>

            {ax.ticks.map((v) => (
              <g key={v}>
                <line x1="0" x2={pw} y1={y(v)} y2={y(v)} className={v === 0 ? 'zero' : 'grid'} />
                <text x={pw + 8} y={y(v)} dy="0.32em" className="axis">
                  {format(v)}
                </text>
              </g>
            ))}

            {hover !== undefined && n > 0 && (
              <g className="cross">
                <rect x={x(hover) - sw / 2} y={PAD_TOP} width={sw} height={ph} />
              </g>
            )}

            {shown.map((s) =>
              s.kind === 'area' ? (
                <g key={s.id} className={`tone-${s.tone ?? 'accent'}`}>
                  <path d={area(s)} className="area" />
                  <path d={path(s)} className="line" />
                </g>
              ) : s.kind === 'pnl' ? (
                <g key={s.id}>
                  <g className="tone-pos">
                    <path d={area(s)} className="area" clipPath={`url(#above${id})`} />
                    <path d={path(s)} className="line" clipPath={`url(#above${id})`} />
                  </g>
                  <g className="tone-neg">
                    <path d={area(s)} className="area" clipPath={`url(#below${id})`} />
                    <path d={path(s)} className="line" clipPath={`url(#below${id})`} />
                  </g>
                </g>
              ) : null,
            )}

            {bars.length > 0 &&
              times.map((_, i) => {
                let top = 0;
                let bottom = 0;
                return bars.map((s) => {
                  const v = drawn(s, i) ?? 0;
                  if (v === 0) return null;
                  const from = v > 0 ? top : bottom;
                  const to = from + v;
                  if (v > 0) top = to;
                  else bottom = to;
                  const y1 = y(Math.max(from, to));
                  const y2 = y(Math.min(from, to));
                  return <rect key={`${s.id}${i}`} x={x(i) - half} y={y1} width={half * 2} height={Math.max(1, y2 - y1)} className={`bar tone-${s.tone ?? 'accent'}`} />;
                });
              })}

            {shown.map((s) =>
              s.kind === 'line' ? (
                <g key={s.id} className={`tone-${s.tone ?? 'accent'}`}>
                  <path d={path(s)} className="line" />
                  {n <= 2 && times.map((_, i) => (drawn(s, i) === undefined ? null : <circle key={i} cx={x(i)} cy={y(drawn(s, i)!)} r={3} className="dot" />))}
                </g>
              ) : null,
            )}

            {hover !== undefined &&
              shown.map((s) => {
                if (s.kind === 'bar') return null;
                const v = drawn(s, hover);
                if (v === undefined) return null;
                const tone = s.kind === 'pnl' ? (v >= 0 ? 'pos' : 'neg') : (s.tone ?? 'accent');
                return <circle key={s.id} cx={x(hover)} cy={y(v)} r={4} className={`dot tone-${tone}`} />;
              })}

            {labels.map((l) => (
              <text key={l.i} x={l.anchor === 'start' ? 0 : l.anchor === 'end' ? pw : x(l.i)} y={height - 5} textAnchor={l.anchor} className="axis">
                {timeLabel(times[l.i]!, bucketMs, longLabels)}
              </text>
            ))}
          </svg>
        )}
        {!hasData && <div className="tchart-empty">{empty}</div>}
        {hover !== undefined && hasData && (
          <div className="tchart-tip" style={tipLeft ? { left: x(hover) + 14 } : { right: w - x(hover) + 14 }}>
            <div className="tchart-tip-time">{timeLabel(times[hover]!, bucketMs, true)}</div>
            {series.map((s) => {
              const v = s.values[hover];
              if (v === undefined || !Number.isFinite(v)) return null;
              const tone = s.kind === 'pnl' ? (v >= 0 ? 'pos' : 'neg') : (s.tone ?? 'accent');
              return (
                <div className="row" key={s.id}>
                  <span className={s.hidden ? '' : `tone-${tone}`}>
                    {!s.hidden && <i />}
                    {s.label}
                  </span>
                  <b className="num">{(s.format ?? format)(v)}</b>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
